/**
 * ConfigFieldRenderer - Renders individual config fields based on type
 *
 * Handles different field types:
 * - text, textarea, number: Basic input controls
 * - select, radio: Options-based controls (static or dynamic)
 * - checkbox: Boolean toggle
 * - slider: Numeric slider
 * - custom: Delegates to custom renderers
 *
 * Supports:
 * - options_source: Dynamic options loading from API
 * - mapping_enabled: Wraps field in FieldSourceToggle
 * - show_when: Visibility controlled by parent (ConfigSection)
 */

import React from 'react';
import type { ConfigFieldDefinition, FieldType } from '@/types/workflowFields';
import type { NodeData, NodeType } from '@/types/imageWorkflow';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { FieldSourceToggle } from './FieldSourceToggle';
import { useOptionsSource } from '../hooks/useOptionsSource';

// Import custom renderers
import {
  BuiltinVariablesRenderer,
  CustomVariablesRenderer,
  ModelConfigRenderer,
  InputModeRenderer,
  ArtifactConfigRenderer,
  CharmOptionsRenderer,
  PushTargetRenderer,
} from './config-renderers';

// ============================================================================
// Types
// ============================================================================

interface FieldSourceHandlers {
  mapping?: {
    input_field_key: string;
    source_node_key: string;
    source_output_key: string;
    source_key_path?: string;
    is_auto_mapped: boolean;
  };
  onMappingChange: (sourceNodeKey: string, sourceOutputKey: string, keyPath?: string) => void;
  onKeyPathChange: (keyPath: string | undefined) => void;
  onMappingRemove: () => void;
}

/** Available source node for field mapping - matches FieldSourceToggle */
interface AvailableSource {
  node_key: string;
  node_type: string;
  node_label: string;
  outputs: Array<{ key: string; type: FieldType; label: string | undefined }>;
}

/** Context variable - matches FieldSourceToggle */
interface ContextVariable {
  key: string;
  label: string;
  type: FieldType;
}

/** Start node custom variable - matches FieldSourceToggle */
interface StartVariable {
  key: string;
  value: string;
}

/** Custom input field */
interface CustomInputField {
  key: string;
  type: FieldType;
  label?: string;
}

/** Factory for creating field handlers (for custom renderers with multiple subfields) */
interface FieldSourceHandlersFactory {
  createFieldHandlers: (fieldKey: string) => FieldSourceHandlers;
}

interface ConfigFieldRendererProps {
  /** Field definition */
  field: ConfigFieldDefinition;
  /** Current field value */
  value: unknown;
  /** Callback when value changes */
  onChange: (value: unknown) => void;
  /** Full node data (for context) */
  nodeData: NodeData;
  /** Node type (for custom renderers that need context) */
  nodeType?: NodeType;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Field source handlers for mapping mode */
  fieldSourceHandlers?: FieldSourceHandlers;
  /** Factory for custom renderers to create handlers for subfields */
  fieldSourceHandlersFactory?: FieldSourceHandlersFactory;
  /** Available sources for mapping */
  availableSources?: AvailableSource[];
  /** Custom inputs for mapping */
  customInputs?: CustomInputField[];
  /** Context variables for mapping */
  contextVariables?: ContextVariable[];
  /** Start variables for mapping */
  startVariables?: StartVariable[];
  /** Callback to update multiple fields at once (for custom renderers) */
  onBatchUpdate?: (updates: Partial<NodeData>) => void;
}

// ============================================================================
// Custom Renderer Registry
// ============================================================================

/**
 * Registry for custom renderers.
 * Each renderer handles a specific complex UI case.
 */
type CustomRenderer = React.FC<{
  field: ConfigFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  nodeData: NodeData;
  nodeType?: NodeType;
  isDisabled?: boolean;
  onBatchUpdate?: (updates: Partial<NodeData>) => void;
  // Mapping support props
  fieldSourceHandlers?: FieldSourceHandlers;
  fieldSourceHandlersFactory?: FieldSourceHandlersFactory;
  availableSources?: AvailableSource[];
  customInputs?: CustomInputField[];
  contextVariables?: ContextVariable[];
  startVariables?: StartVariable[];
}>;

const CUSTOM_RENDERERS: Record<string, CustomRenderer> = {
  'builtin-variables': BuiltinVariablesRenderer,
  'custom-variables': CustomVariablesRenderer,
  'model-config': ModelConfigRenderer,
  'input-mode': InputModeRenderer,
  'artifact-config': ArtifactConfigRenderer,
  'charm-options': CharmOptionsRenderer,
  'push-target': PushTargetRenderer,
};

// ============================================================================
// Component
// ============================================================================

export const ConfigFieldRenderer: React.FC<ConfigFieldRendererProps> = ({
  field,
  value,
  onChange,
  nodeData,
  nodeType,
  isDisabled = false,
  fieldSourceHandlers,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
  onBatchUpdate,
}) => {
  // Load dynamic options if options_source is specified
  const { options: dynamicOptions, isLoading: optionsLoading } = useOptionsSource(
    field.options_source,
    nodeData
  );

  // Resolve options: static options or dynamic options
  const resolvedOptions = field.options_source ? dynamicOptions : field.options || [];

  // Render the appropriate control based on field type
  const renderControl = () => {
    switch (field.type) {
      case 'text':
        return (
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.description}
            disabled={isDisabled}
            required={field.required}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.description}
            disabled={isDisabled}
            required={field.required}
            rows={4}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value !== undefined ? String(value) : ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
            placeholder={field.description}
            disabled={isDisabled}
            required={field.required}
          />
        );

      case 'select':
        return (
          <Select
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled || optionsLoading}
            required={field.required}
            options={[
              { value: '', label: optionsLoading ? '加载中...' : '请选择...' },
              ...resolvedOptions,
            ]}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={isDisabled}
            label={field.description}
          />
        );

      case 'radio':
        return (
          <div className="flex flex-wrap gap-3">
            {resolvedOptions.map((option) => (
              <label
                key={option.value}
                className={`
                  inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer
                  transition-colors
                  ${value === option.value
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                  }
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <input
                  type="radio"
                  name={field.key}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={isDisabled}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-2">
            <input
              type="range"
              min={0}
              max={100}
              value={(value as number) || 50}
              onChange={(e) => onChange(Number(e.target.value))}
              disabled={isDisabled}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="text-xs text-gray-500 text-center">{(value as number) ?? 50}</div>
          </div>
        );

      case 'custom':
        // Delegate to custom renderer
        if (field.renderer && CUSTOM_RENDERERS[field.renderer]) {
          const CustomRendererComponent = CUSTOM_RENDERERS[field.renderer];
          return (
            <CustomRendererComponent
              field={field}
              value={value}
              onChange={onChange}
              nodeData={nodeData}
              nodeType={nodeType}
              isDisabled={isDisabled}
              onBatchUpdate={onBatchUpdate}
              // Pass mapping props to custom renderers
              fieldSourceHandlers={fieldSourceHandlers}
              fieldSourceHandlersFactory={fieldSourceHandlersFactory}
              availableSources={availableSources}
              customInputs={customInputs}
              contextVariables={contextVariables}
              startVariables={startVariables}
            />
          );
        }
        // Fallback for unknown custom renderer
        return (
          <div className="text-sm text-gray-400 italic">
            未实现的渲染器: {field.renderer}
          </div>
        );

      default:
        // Fallback to text input for unknown types
        return (
          <Input
            type="text"
            value={String(value || '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.description}
            disabled={isDisabled}
          />
        );
    }
  };

  // If mapping is enabled, wrap in FieldSourceToggle
  if (field.mapping_enabled && fieldSourceHandlers) {
    return (
      <FieldSourceToggle
        fieldKey={field.key}
        label={field.label}
        description={field.description}
        required={field.required}
        availableSources={availableSources}
        customInputs={customInputs}
        contextVariables={contextVariables}
        startVariables={startVariables}
        mapping={fieldSourceHandlers.mapping}
        onMappingChange={fieldSourceHandlers.onMappingChange}
        onKeyPathChange={fieldSourceHandlers.onKeyPathChange}
        onMappingRemove={fieldSourceHandlers.onMappingRemove}
        disabled={isDisabled}
        compatibleTypes={field.compatible_types}
      >
        {renderControl()}
      </FieldSourceToggle>
    );
  }

  // Without mapping, render with label wrapper
  if (field.type === 'checkbox') {
    // Checkbox has its own label
    return renderControl();
  }

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {renderControl()}
      {field.description && (
        <p className="text-xs text-gray-500">{field.description}</p>
      )}
    </div>
  );
};

// ============================================================================
// Custom Renderer Registration
// ============================================================================

/**
 * Register a custom renderer for a specific renderer name.
 * This allows custom renderers to be added dynamically.
 */
export function registerCustomRenderer(name: string, renderer: CustomRenderer): void {
  CUSTOM_RENDERERS[name] = renderer;
}

export default ConfigFieldRenderer;
